import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import userService from '../../services/UserService.js';
import companyService from '../../services/CompanyService.js';
import axiosInstance from '../../config/axios.js';
import { Users, Plus, FileSpreadsheet } from "lucide-react";
import BulkExcelModal from '../common/BulkExcelModal.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import UserAvatar from '../common/UserAvatar.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import isExternalNetworkEnabled, { isContractPartyAdmin } from '../../utils/networkMode.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { useTranslation } from 'react-i18next';
import { getUserPartyLabel, showUserPartyLabel } from '../../utils/userCreatableRoles.js';
import { canEditUserRecord } from '../../utils/companyAccess.js';
import { listScopeTabClass } from '../common/uiClasses.js';
import { stripHubPrefixFromLabel } from '../../utils/destinationType.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';

const HubName = ({ value }) => {
  const label = stripHubPrefixFromLabel(value) || 'N/A';
  return (
    <span className="block text-sm text-gray-900 whitespace-normal break-words leading-snug" title={label}>
      {label}
    </span>
  );
};

const UserList = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [agentListTab, setAgentListTab] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const perPage = 10;
  const userRole = currentUser?.role_name || currentUser?.role;
  const isCpaView = isContractPartyAdmin(currentUser);

  // Role-based permissions
  const canManageUsers = ['Admin', 'System Admin', 'Accountant', 'Contract Party Admin'].includes(userRole);
  const canAddUsers = ['Admin', 'System Admin', 'Accountant'].includes(userRole);
  const [userExcelOpen, setUserExcelOpen] = useState(false);
  const isSystemAdmin = userRole === 'System Admin';
  const showAgentScopeTabs = (isExternalNetworkEnabled(currentUser) || isContractPartyAdmin(currentUser) || currentUser?.is_cargo_shipment)
    && canManageUsers
    && !isCpaView;

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      setRoles(response.data.data);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAll(1, 1000, {});
      const companiesData = response.data || [];
      setCompanies(companiesData);
    } catch (err) {
      console.error('Failed to load companies', err);
    }
  };

  const fetchLocationHubs = async () => {
    try {
      const response = await axiosInstance.get('/location-hubs', {
        params: { all: 'true' }
      });
      const hubs = response.data.hubs || [];
      
      // Extract unique cities from location hubs
      const uniqueCities = [...new Set(hubs.map(hub => hub.location))].sort();
      setLocationHubs(uniqueCities);
    } catch (err) {
      console.error('Failed to load location hubs', err);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (roleFilter) filters.role_id = roleFilter;
      if (companyFilter) filters.company_id = companyFilter;
      if (cityFilter) filters.city = cityFilter;
      if (stateFilter) filters.state = stateFilter;
      if (isCpaView) {
        const agentRole = roles.find((role) => role.role_name === 'Agent');
        if (agentRole) filters.role_id = agentRole.id;
        filters.agent_scope = 'party';
      } else if (agentListTab) {
        const agentRole = roles.find((r) => r.role_name === 'Agent');
        if (agentRole) filters.role_id = agentRole.id;
        filters.agent_scope = agentListTab;
      }

      const response = await userService.getAll(currentPage, perPage, filters);
      setUsers((prev) => mergeLoadedPage(currentPage, response.data, prev));
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, roleFilter, companyFilter, cityFilter, stateFilter, agentListTab, currentPage, perPage, roles, isCpaView]);

  usePaginatedListFetch({
    fetchPage: fetchUsers,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, statusFilter, roleFilter, companyFilter, cityFilter, stateFilter, agentListTab],
    debounceWhen: search,
    debounceMs: 0,
  });

  useEffect(() => {
    fetchRoles();
    fetchLocationHubs();
  }, []);

  useEffect(() => {
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [isSystemAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const canEditThisUser = (user) => {
    if (!canManageUsers) return false;
    return canEditUserRecord(user, currentUser);
  };

  const getProfileImageUrl = (user) => {
    return user.profile_photo_url || '';
  };

  const toggleDropdown = (userId) => {
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
  };

  const handleDelete = async (userId, currentStatus, userName) => {
    const action = currentStatus === 'active' ? 'deactivate' : 'activate';
    const confirmation = window.confirm(
      `Are you sure you want to ${action} user "${userName}"?`
    );

    if (confirmation) {
      try {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        await userService.update(userId, { status: newStatus });
        Alert.success(`User ${action}d successfully!`);
        fetchUsers();
      } catch (err) {
        Alert.error(`Failed to ${action} user`);
        console.error(err);
      }
    }
  };

  const hasActiveFilters = search || statusFilter || roleFilter || companyFilter || cityFilter || stateFilter;

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
    setCompanyFilter('');
    setCityFilter('');
    setStateFilter('');
  };

  return (
    <DashboardLayout fullWidth>
      <div className={`w-full ${isCpaView ? 'md:bg-white md:rounded-xl md:shadow-sm md:p-4 lg:p-6' : ''}`}>
              <ListPageHeader
                title="User Management"
                subtitle={isCpaView ? 'Manage your contract party agents' : 'Manage all system users'}
                icon={Users}
                titleClassName="text-primary"
                iconClassName="w-6 h-6 sm:w-7 sm:h-7 shrink-0"
                className={isCpaView ? 'mb-4 md:mb-5' : 'mb-4 sm:mb-6'}
              >
                {canAddUsers && (
                  <>
                    <Button mobileIconOnly variant="secondary" icon={FileSpreadsheet} onClick={() => setUserExcelOpen(true)}>
                      Excel
                    </Button>
                    <Button mobileIconOnly icon={Plus} onClick={() => navigate('/users/add')}>
                      Add User
                    </Button>
                  </>
                )}
              </ListPageHeader>

              {showAgentScopeTabs && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { id: '', label: 'All Users' },
                    { id: 'company', label: 'Own Company Agents' },
                    ...(currentUser?.is_cargo_shipment || isContractPartyAdmin(currentUser) ? [{ id: 'party', label: 'Contract Party Agents' }] : []),
                    ...(isExternalNetworkEnabled(currentUser) ? [{ id: 'network', label: 'Network Agents' }] : []),
                  ].map((tab) => (
                    <button
                      key={tab.id || 'all'}
                      type="button"
                      onClick={() => { setAgentListTab(tab.id); setCurrentPage(1); }}
                      className={listScopeTabClass(agentListTab === tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              <ListFilterPanel
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearFilters}
                className={isCpaView ? 'md:!bg-transparent md:!shadow-none md:!p-0 md:!mb-4' : ''}
              >
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('common.search')}</label>
                  <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Search by name, email, ID..."
              inputClassName="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                {!isCpaView && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  >
                    <option value="">All Roles</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.role_name}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                {isSystemAdmin && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('common.company')}</label>
                    <select
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                    >
                      <option value="">{t('common.allCompanies')}</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.company_name} ({company.company_id})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('common.hub')}</label>
                  <select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  >
                    <option value="">{t('dashboard.allHubs')}</option>
                    {locationHubs.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    placeholder="Filter by state"
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  />
                </div>
              </ListFilterPanel>

              <div className={`bg-white rounded-xl shadow-sm w-full ${isCpaView ? 'md:shadow-none md:rounded-none' : ''}`}>
                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading users..." />
                  </div>
                ) : (
                  <>
                {/* Table View - Desktop / large screens */}
                <div className="hidden xl:block w-full overflow-x-auto">
                  {users.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">
                      No users found
                    </div>
                  ) : (
                    <>
                      <table className="w-full table-fixed divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="w-[16%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">User</th>
                              <th className="w-[16%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Contact</th>
                              <th className="w-[14%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.company')}</th>
                              <th className="w-[14%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.hub')}</th>
                              <th className="w-[12%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('forms.state')}</th>
                              <th className="w-[10%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Role</th>
                              <th className="w-[10%] px-4 2xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                              <th className="w-[8%] px-4 2xl:px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                              <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 2xl:px-6 py-4 whitespace-nowrap">
                                  <div
                                    className="flex items-center gap-3 cursor-pointer"
                                    onClick={() => navigate(`/users/view/${user.user_id}`)}
                                  >
                                    <div className="relative">
                                      <UserAvatar
                                        src={getProfileImageUrl(user)}
                                        alt={user.name}
                                        size="md"
                                      />
                                      {/* Online/Offline Status Indicator on Profile Image */}
                                      <div className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4">
                                        <div
                                          className={`w-3 h-3 rounded-full border-2 border-white ${
                                            user.is_online ? 'bg-green-500' : 'bg-red-500'
                                          }`}
                                          title={user.is_online ? 'Online' : 'Offline'}
                                        >
                                          {user.is_online && (
                                            <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full opacity-75 animate-ping"></div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                                      <div className="text-xs text-gray-600">{user.username || '—'}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 2xl:px-6 py-4">
                                  <div className="text-sm text-gray-900 truncate">{user.email}</div>
                                  <div className="text-xs text-gray-600 truncate">{user.contact}</div>
                                </td>
                                <td className="px-4 2xl:px-6 py-4">
                                  <div className="text-sm text-gray-900 truncate">{user.company_name || 'N/A'}</div>
                                  <div className="text-xs text-gray-600 truncate">{user.company_id || 'N/A'}</div>
                                </td>
                                <td className="px-4 2xl:px-6 py-4 align-top">
                                  <HubName value={user.location_hub_label || user.location_hub_name || user.city} />
                                </td>
                                <td className="px-4 2xl:px-6 py-4">
                                  <div className="text-sm text-gray-900 truncate">{user.state || 'N/A'}</div>
                                </td>
                                <td className="px-4 2xl:px-6 py-4 whitespace-nowrap">
                                  <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-700">
                                    {user.role_name}
                                  </span>
                                  {showUserPartyLabel(user.role_name) && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {getUserPartyLabel(user)}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 2xl:px-6 py-4 whitespace-nowrap">
                                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    user.status === 'active'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {capitalizeStatus(user.status)}
                                  </span>
                                </td>
                                <td className="px-4 2xl:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <RowActionsMenu
                                    isOpen={activeDropdown === user.user_id}
                                    onToggle={() => toggleDropdown(user.user_id)}
                                    onClose={() => setActiveDropdown(null)}
                                    menuRef={dropdownRef}
                                    onView={() => navigate(`/users/view/${user.user_id}`)}
                                    onEdit={canEditThisUser(user) ? () => navigate(`/users/edit/${user.user_id}`) : undefined}
                                    extraItems={
                                      canEditThisUser(user)
                                        ? [
                                            {
                                              label: user.status === 'active' ? 'Deactivate User' : 'Activate User',
                                              icon: user.status === 'active' ? '🚫' : '✅',
                                              variant: user.status === 'active' ? 'danger' : 'success',
                                              onClick: () => handleDelete(user.user_id, user.status, user.name),
                                            },
                                          ]
                                        : []
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    </>
                  )}
                </div>

                {/* Card View - Mobile & iPad */}
                <div className="xl:hidden p-3 sm:p-4">
                  {users.length === 0 ? (
                    <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">No users found</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      {users.map((user) => (
                        <div key={user.user_id} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5 border-l-4 border-l-blue-500">
                          <div className="flex items-start gap-3 sm:gap-4 mb-3">
                            <div className="relative flex-shrink-0">
                              <UserAvatar
                                src={getProfileImageUrl(user)}
                                alt={user.name}
                                size="lg"
                              />
                              {/* Online/Offline Status Indicator on Profile Image - Mobile */}
                              <div className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4">
                                <div
                                  className={`w-4 h-4 rounded-full border-2 border-white ${
                                    user.is_online ? 'bg-green-500' : 'bg-red-500'
                                  }`}
                                  title={user.is_online ? 'Online' : 'Offline'}
                                >
                                  {user.is_online && (
                                    <div className="absolute inset-0 w-4 h-4 bg-green-500 rounded-full opacity-75 animate-ping"></div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{user.name}</h3>
                              <p className="text-xs sm:text-sm text-gray-600">{user.username || '—'}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                                  {user.role_name}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                                user.status === 'active'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {capitalizeStatus(user.status)}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === user.user_id}
                                onToggle={() => toggleDropdown(user.user_id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/users/view/${user.user_id}`)}
                                onEdit={canEditThisUser(user) ? () => navigate(`/users/edit/${user.user_id}`) : undefined}
                                extraItems={
                                  canEditThisUser(user)
                                    ? [
                                        {
                                          label: user.status === 'active' ? 'Deactivate User' : 'Activate User',
                                          icon: user.status === 'active' ? '🚫' : '✅',
                                          variant: user.status === 'active' ? 'danger' : 'success',
                                          onClick: () => handleDelete(user.user_id, user.status, user.name),
                                        },
                                      ]
                                    : []
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs sm:text-sm">
                            <div>
                              <p className="text-xs text-gray-500 font-medium">Contact</p>
                              <p className="text-gray-900">{user.contact || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium">Email</p>
                              <p className="text-gray-900 truncate">{user.email || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium">{t('common.company')}</p>
                              <p className="text-gray-900 truncate">{user.company_name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium">{t('common.hub')}</p>
                              <HubName value={user.location_hub_label || user.location_hub_name || user.city} />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium">{t('forms.state')}</p>
                              <p className="text-gray-900 truncate">{user.state || 'N/A'}</p>
                            </div>
                            {showUserPartyLabel(user.role_name) && (
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Party</p>
                                <p className="text-gray-900 truncate">{getUserPartyLabel(user)}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {users.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-600">
                      Showing {users.length} user{users.length !== 1 ? 's' : ''} · Page {currentPage} of {totalPages}
                    </p>
                    <ListPagination
                                          currentPage={currentPage}
                                          totalPages={totalPages}
                                          loading={loading && currentPage > 1}
                                          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                                        />
                  </div>
                )}
                  </>
                )}
              </div>
      </div>
      <BulkExcelModal
        open={userExcelOpen}
        onClose={() => setUserExcelOpen(false)}
        title="Users Excel"
        description="Download the users already saved, or an empty sheet. Passwords are left blank. Upload to add more users. A blank password gets a one-time password on this screen. Existing email, username, or contact is skipped."
        onDownloadCurrent={async () => {
          const res = await userService.downloadBulkExport(companyFilter || null);
          await downloadBlob(
            res.data,
            'users_export.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
        }}
        currentLabel="Download current users"
        onDownloadTemplate={async () => {
          const res = await userService.downloadBulkTemplate();
          await downloadBlob(
            res.data,
            'users_bulk.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
        }}
        onUploadFile={(file) => userService.uploadBulkExcel(file, companyFilter || null)}
        onImported={() => fetchUsers()}
      />
    </DashboardLayout>
  );
};

export default UserList;